import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, EVENTS, Joyride, STATUS } from 'react-joyride';
import type { EventData, Placement, Step, TooltipRenderProps } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isActiveOrganizationAdmin, isSuperAdminUser } from '../utils/roles';
import {
    DASHBOARD_GUIDED_TOUR_STEPS,
    GuidedTourRouteContext,
    getGuidedTourStepIndexById,
    resolveGuidedTourRoute,
    resolveGuidedTourTarget,
} from './dashboardGuidedTourConfig';
import {
    GUIDED_TOUR_STORAGE_KEYS,
    getStoredTourStepIndex,
    getTourStorageValue,
    isDashboardGuidedTourDismissed,
    removeTourStorageValue,
    setStoredTourStepIndex,
    setTourStorageValue,
} from './guidedTourStorage';
import './GuidedTour.css';

interface GuidedTourContextValue
{
    startGuidedTour: () => void;
    skipGuidedTour: () => void;
    isGuidedTourRunning: boolean;
    isGuidedTourAvailable: boolean;
}

const GuidedTourContext = createContext<GuidedTourContextValue | undefined>(undefined);
const ORGANIZATION_CREATE_MODAL_EVENT = 'acceliqa:guided-tour-open-organization-create';
const BILLING_PAYMENT_FORM_EVENT = 'acceliqa:guided-tour-show-billing-payment-form';
const PROJECT_WIZARD_STEP_EVENT = 'acceliqa:guided-tour-project-step';
const PROJECT_WIZARD_STEP_CHANGED_EVENT = 'acceliqa:guided-tour-project-step-changed';
const PROJECT_WIZARD_SHOW_AZURE_EVENT = 'acceliqa:guided-tour-show-azure-devops-fields';
const REQUIREMENTS_GENERATE_REVIEW_EVENT = 'acceliqa:guided-tour-open-requirements-review';

function routeMatches(expectedRoute: string, pathname: string, search: string): boolean
{
    const [expectedPath, expectedQuery] = expectedRoute.split('?');
    if (pathname !== expectedPath)
    {
        return false;
    }

    if (!expectedQuery)
    {
        return true;
    }

    const expectedParams = new URLSearchParams(expectedQuery);
    const actualParams = new URLSearchParams(search);
    let matches = true;

    expectedParams.forEach((value, key) =>
    {
        if (actualParams.get(key) !== value)
        {
            matches = false;
        }
    });

    return matches;
}

function routePathMatches(expectedRoute: string, pathname: string): boolean
{
    const [expectedPath] = expectedRoute.split('?');
    return pathname === expectedPath;
}

function getProjectWizardStepStartIndex(
    wizardStep: 1 | 2 | 3 | 4,
    routeContext: GuidedTourRouteContext,
    pathname: string,
): number
{
    return DASHBOARD_GUIDED_TOUR_STEPS.findIndex((step) =>
        step.projectWizardStep === wizardStep
        && routePathMatches(resolveGuidedTourRoute(step, routeContext), pathname));
}

function isGuidedTourQuery(search: string): boolean
{
    return new URLSearchParams(search).get('guidedTour') === '1';
}

function getSearchWithoutGuidedTour(search: string): string
{
    const params = new URLSearchParams(search);
    params.delete('guidedTour');
    const nextSearch = params.toString();
    return nextSearch ? `?${nextSearch}` : '';
}

function isVisibleTourTarget(target: Element | null): target is Element
{
    if (!target)
    {
        return false;
    }

    const rect = target.getBoundingClientRect();
    return target.getClientRects().length > 0 && rect.width > 0 && rect.height > 0;
}

function getTargetInputValue(selector: string): string
{
    const target = document.querySelector(selector);
    if (!target)
    {
        return '';
    }

    const input = target.matches('input, textarea')
        ? target
        : target.querySelector('input, textarea');

    return input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement
        ? input.value.trim()
        : '';
}

function getTargetFieldElement(selector: string): HTMLElement | null
{
    const target = document.querySelector(selector);
    if (!target)
    {
        return null;
    }

    const field = target.matches('input, textarea, select')
        ? target
        : target.querySelector('input, textarea, select');

    return field instanceof HTMLElement ? field : null;
}

function getTargetSelectValue(selector: string): string
{
    const target = document.querySelector(selector);
    if (!target)
    {
        return '';
    }

    const select = target.matches('select')
        ? target
        : target.querySelector('select');

    return select instanceof HTMLSelectElement ? select.value.trim() : '';
}

function hasCheckedTarget(selector: string): boolean
{
    const target = document.querySelector(selector);
    if (!target)
    {
        return false;
    }

    if (target instanceof HTMLInputElement && target.checked)
    {
        return true;
    }

    return Boolean(target.querySelector('input[type="checkbox"]:checked'));
}

function focusTourTarget(target: Element): void
{
    try
    {
        const focusSelectors = [
            'input:not([type="hidden"]):not([disabled])',
            'textarea:not([disabled])',
            'select:not([disabled])',
            'button:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(', ');

        let focusEl: HTMLElement | null = null;

        if (target instanceof HTMLElement && target.matches(focusSelectors))
        {
            focusEl = target;
        }
        else
        {
            focusEl = target.querySelector<HTMLElement>(focusSelectors);
        }

        if (focusEl)
        {
            focusEl.focus({ preventScroll: true });
        }
    }
    catch
    {
        /* Focus failures should not interrupt the tour. */
    }
}

function clearGuidedTourValidationHighlights(): void
{
    document.querySelectorAll('.guided-tour-required-missing, .guided-tour-required-field-missing')
        .forEach((element) =>
        {
            element.classList.remove('guided-tour-required-missing');
            element.classList.remove('guided-tour-required-field-missing');
        });
}

function isGuidedTourRequiredTargetSatisfied(target: Element): boolean
{
    const field = target.matches('input, textarea, select')
        ? target
        : target.querySelector('input, textarea, select');
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)
    {
        return field.value.trim().length > 0;
    }

    if (target.matches('[role="radio"]') || target.querySelector('[role="radio"]'))
    {
        const option = target.matches('[role="radio"]')
            ? target
            : target.querySelector('[role="radio"]');
        return option instanceof HTMLElement
            && (
                option.getAttribute('aria-checked') === 'true'
                || option.classList.contains('project-type-card-selected')
            );
    }

    return false;
}

function isGuidedTourRequiredSelectorSatisfied(selector: string): boolean
{
    const target = document.querySelector(selector);
    return target ? isGuidedTourRequiredTargetSatisfied(target) : true;
}

function clearSatisfiedGuidedTourValidationHighlights(): void
{
    document.querySelectorAll('.guided-tour-required-missing')
        .forEach((target) =>
        {
            if (!isGuidedTourRequiredTargetSatisfied(target))
            {
                return;
            }

            target.classList.remove('guided-tour-required-missing');
            target.querySelectorAll('.guided-tour-required-field-missing')
                .forEach((field) => field.classList.remove('guided-tour-required-field-missing'));
        });

    document.querySelectorAll('.guided-tour-required-field-missing')
        .forEach((field) =>
        {
            if (
                (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)
                && field.value.trim().length > 0
            )
            {
                field.classList.remove('guided-tour-required-field-missing');
            }
        });
}

function getRequiredTargetSelectorsForStep(stepId: string | undefined): string[]
{
    if (stepId === 'organization-name')
    {
        return ['#organization-create-name'];
    }

    if (stepId === 'project-name')
    {
        return ['#project-create-name'];
    }

    if (stepId === 'project-azure-option')
    {
        return ['#project-type-azure'];
    }

    if (stepId === 'project-azure-organization')
    {
        return ['#project-create-azure-org'];
    }

    if (stepId === 'project-azure-project')
    {
        return ['#project-create-azure-project'];
    }

    if (stepId === 'project-azure-team')
    {
        return ['#project-create-azure-team'];
    }

    if (stepId === 'project-azure-pat')
    {
        return ['#project-create-azure-pat'];
    }

    if (stepId === 'project-step-one-next')
    {
        const selectors = ['#project-create-name'];
        if (getSelectedProjectType() === 'azure_devops')
        {
            selectors.push(
                '#project-create-azure-org',
                '#project-create-azure-project',
                '#project-create-azure-team',
                '#project-create-azure-pat',
            );
        }
        return selectors;
    }

    return [];
}

function markGuidedTourRequiredFields(stepId: string | undefined): void
{
    clearGuidedTourValidationHighlights();
    const selectors = getRequiredTargetSelectorsForStep(stepId);
    let firstInvalidField: HTMLElement | null = null;

    selectors.forEach((selector) =>
    {
        const target = document.querySelector(selector);
        if (!(target instanceof HTMLElement))
        {
            return;
        }

        const field = getTargetFieldElement(selector);
        if (isGuidedTourRequiredSelectorSatisfied(selector))
        {
            return;
        }

        target.classList.add('guided-tour-required-missing');
        field?.classList.add('guided-tour-required-field-missing');
        firstInvalidField = firstInvalidField || field || target;
    });

    if (firstInvalidField)
    {
        firstInvalidField.focus({ preventScroll: true });
    }
}

function inferCurrentProjectRoute(pathname: string, search: string): string | null
{
    if (!pathname.startsWith('/projects/') || pathname === '/projects/new')
    {
        return null;
    }

    if (pathname.includes('/repository'))
    {
        return null;
    }

    const match = pathname.match(/^\/projects\/[^/]+/);
    if (!match)
    {
        return null;
    }

    const projectBaseRoute = match[0];
    const searchParams = new URLSearchParams(search);
    const activeTab = searchParams.get('tab');

    if (pathname === projectBaseRoute && activeTab === 'requirements')
    {
        return `${projectBaseRoute}?tab=requirements`;
    }

    return `${projectBaseRoute}?tab=requirements`;
}

function getSelectedProjectType(): 'simple' | 'azure_devops'
{
    const azureOption = document.querySelector('#project-type-azure');
    const isAzureSelected = azureOption instanceof HTMLElement
        && (
            azureOption.getAttribute('aria-checked') === 'true'
            || azureOption.classList.contains('project-type-card-selected')
        );

    return isAzureSelected ? 'azure_devops' : 'simple';
}

function hasProjectName(): boolean
{
    return Boolean(getTargetInputValue('#project-create-name-input'));
}

function hasAzureDevOpsProjectFields(): boolean
{
    if (getSelectedProjectType() !== 'azure_devops')
    {
        return true;
    }

    return Boolean(
        getTargetInputValue('#project-create-azure-org-input')
        && getTargetInputValue('#project-create-azure-project-input')
        && getTargetInputValue('#project-create-azure-team-input')
        && getTargetInputValue('#project-create-azure-pat-input')
    );
}

function shouldSkipGuidedTourStep(stepId: string | undefined): boolean
{
    if (!stepId)
    {
        return false;
    }

    if ([
        'billing-payment-method',
        'billing-card-details',
        'billing-save-payment-method',
    ].includes(stepId))
    {
        return document.querySelector('#billing-payment-method-card[data-has-payment-method="true"]') !== null;
    }

    return [
        'project-azure-organization',
        'project-azure-project',
        'project-azure-team',
        'project-azure-pat',
    ].includes(stepId) && getSelectedProjectType() !== 'azure_devops';
}

function getNextStepIndex(stepId: string | undefined, currentStepIndex: number): number
{
    if (stepId === 'project-type')
    {
        return getSelectedProjectType() === 'azure_devops'
            ? getGuidedTourStepIndexById('project-azure-option')
            : getGuidedTourStepIndexById('project-simple-option');
    }

    if (stepId === 'project-simple-option')
    {
        return getGuidedTourStepIndexById('project-step-one-next');
    }

    if (stepId === 'project-azure-option')
    {
        return getGuidedTourStepIndexById('project-azure-organization');
    }

    return currentStepIndex + 1;
}

function resolveTooltipPlacement(step: Step | undefined, target: Element | null): Placement
{
    const basePlacement = (step?.placement ?? 'bottom') as Placement;

    if (!target)
    {
        return basePlacement;
    }

    const hasOpenDropdown = Boolean(
        target.matches('.cd-root[data-dropdown-open="true"]')
        || target.querySelector('.cd-root[data-dropdown-open="true"], .cd-btn.cd-open, .dropdown-menu, [role="listbox"]')
    );
    if (!hasOpenDropdown)
    {
        return basePlacement;
    }

    if (basePlacement.startsWith('bottom'))
    {
        return 'top';
    }

    if (basePlacement.startsWith('top'))
    {
        return 'bottom';
    }

    return basePlacement;
}

function clampStepIndex(stepIndex: number): number
{
    return Math.max(0, Math.min(stepIndex, DASHBOARD_GUIDED_TOUR_STEPS.length - 1));
}

function getMilestoneComplete(milestone: string | undefined): boolean
{
    if (!milestone)
    {
        return true;
    }

    if (milestone === 'organizationCreated')
    {
        return getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.organizationCreated) === 'true';
    }

    if (milestone === 'billingDecisionCompleted')
    {
        return getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted) === 'true';
    }

    if (milestone === 'projectCreated')
    {
        return Boolean(getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.projectRoute));
    }

    return true;
}

function getStepActionComplete(stepId: string | undefined, hasOrganization: boolean): boolean
{
    if (stepId === 'organization-name')
    {
        return Boolean(getTargetInputValue('#organization-create-name-input'));
    }

    if (stepId === 'project-azure-option')
    {
        return getSelectedProjectType() === 'azure_devops';
    }

    if (stepId === 'project-name')
    {
        return hasProjectName();
    }

    if (stepId === 'project-azure-organization')
    {
        return Boolean(getTargetInputValue('#project-create-azure-org-input'));
    }

    if (stepId === 'project-azure-project')
    {
        return Boolean(getTargetInputValue('#project-create-azure-project-input'));
    }

    if (stepId === 'project-azure-team')
    {
        return Boolean(getTargetInputValue('#project-create-azure-team-input'));
    }

    if (stepId === 'project-azure-pat')
    {
        return Boolean(getTargetInputValue('#project-create-azure-pat-input'));
    }

    if (stepId === 'project-step-one-next')
    {
        return hasProjectName() && hasAzureDevOpsProjectFields();
    }

    if (stepId === 'organization-submit')
    {
        return hasOrganization || getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.organizationCreated) === 'true';
    }

    if (stepId === 'billing-subscribe')
    {
        return getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted) === 'true';
    }

    if (stepId === 'project-submit')
    {
        return Boolean(getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.projectRoute));
    }

    if (stepId === 'upload-docs-dropzone')
    {
        return isVisibleTourTarget(document.querySelector('#requirements-upload-file-0'));
    }

    if (stepId === 'upload-docs-classification')
    {
        return Boolean(getTargetSelectValue('#requirements-upload-file-classification'));
    }

    if (stepId === 'upload-docs-context')
    {
        return Boolean(getTargetInputValue('#requirements-upload-file-context'));
    }

    if (stepId === 'upload-docs-submit' || stepId === 'requirements-document-selection')
    {
        return isVisibleTourTarget(document.querySelector('#requirements-document-selection-first-ready'));
    }

    if (stepId === 'requirements-document-selection-ready')
    {
        return hasCheckedTarget('#requirements-document-selection');
    }

    if (stepId === 'generate-tests-button')
    {
        return isVisibleTourTarget(document.querySelector('#requirements-review-confirm-generate'));
    }

    if (stepId === 'generate-tests-confirm')
    {
        return isVisibleTourTarget(document.querySelector('#requirements-review-confirm-generate'));
    }

    return true;
}

function isRequiredCurrentPageTourStep(stepId: string | undefined): boolean
{
    if (!stepId)
    {
        return false;
    }

    if ([
        'organization-name',
        'project-name',
    ].includes(stepId))
    {
        return true;
    }

    return getSelectedProjectType() === 'azure_devops' && [
        'project-azure-organization',
        'project-azure-project',
        'project-azure-team',
        'project-azure-pat',
        'project-step-one-next',
    ].includes(stepId);
}

function getTooltipArrowPlacement(placement: string | undefined): 'top' | 'bottom'
{
    return placement?.startsWith('top') ? 'top' : 'bottom';
}

interface GuidedTourStepData
{
    stepIndex?: number;
    totalSteps?: number;
    hasOrganization?: boolean;
    requiresMilestone?: string;
}

function GuidedTourTooltip(
    {
        backProps,
        closeProps,
        index,
        primaryProps,
        size,
        skipProps,
        step,
        tooltipProps,
    }: TooltipRenderProps,
): React.ReactElement
{
    const stepData = (step.data as GuidedTourStepData | undefined);
    const currentStepIndex = stepData?.stepIndex ?? index;
    const totalSteps = stepData?.totalSteps ?? size;
    const arrowPlacement = getTooltipArrowPlacement(step.placement);
    const stepId = typeof step.id === 'string' ? step.id : undefined;

    const handlePrimaryClick = (event: React.MouseEvent<HTMLButtonElement>): void =>
    {
        if (
            !getMilestoneComplete(stepData?.requiresMilestone)
            || !getStepActionComplete(stepId, Boolean(stepData?.hasOrganization))
        )
        {
            event.preventDefault();
            event.stopPropagation();
            markGuidedTourRequiredFields(stepId);
            return;
        }

        primaryProps.onClick?.(event);
    };

    return (
        <div
            className={`guided-tour-tooltip ps-tutorial-banner ps-tutorial-banner--${arrowPlacement}`}
            {...tooltipProps}
        >
            <div
                className={`ps-tutorial-arrow ps-tutorial-arrow--${arrowPlacement}`}
                aria-hidden="true"
            >
                <span className="ps-tutorial-arrow-line" />
                <span className="ps-tutorial-arrow-head" />
            </div>

            <button
                type="button"
                className="guided-tour-close"
                {...closeProps}
            >
                ×
            </button>

            {step.title && <h3 className="ps-tutorial-title">{step.title}</h3>}
            <p className="ps-tutorial-body">{step.content}</p>

            <div className="guided-tour-progress">
                Step {currentStepIndex + 1} of {totalSteps}
            </div>

            <div className="ps-tutorial-actions">
                <button
                    type="button"
                    className="ps-tutorial-action ps-tutorial-action--ghost"
                    aria-label={skipProps['aria-label']}
                    data-action={skipProps['data-action']}
                    onClick={skipProps.onClick}
                    title={skipProps.title}
                >
                    Skip Tutorial
                </button>

                {currentStepIndex > 0 && (
                    <button
                        type="button"
                        className="ps-tutorial-action ps-tutorial-action--ghost"
                        aria-label={backProps['aria-label']}
                        data-action={backProps['data-action']}
                        onClick={backProps.onClick}
                        title={backProps.title}
                    >
                        Previous
                    </button>
                )}

                <button
                    type="button"
                    className="ps-tutorial-action ps-tutorial-action--primary"
                    aria-label={primaryProps['aria-label']}
                    data-action={primaryProps['data-action']}
                    onClick={handlePrimaryClick}
                    title={primaryProps.title}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export function useGuidedTour(): GuidedTourContextValue
{
    const context = useContext(GuidedTourContext);
    if (!context)
    {
        throw new Error('useGuidedTour must be used within GuidedTourProvider');
    }
    return context;
}

interface GuidedTourProviderProps
{
    children: React.ReactNode;
}

export function GuidedTourProvider({ children }: GuidedTourProviderProps): React.ReactElement
{
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, loading } = useAuth();
    const [stepIndex, setStepIndex] = useState<number>(() => clampStepIndex(getStoredTourStepIndex()));
    const [running, setRunning] = useState<boolean>(false);
    const [targetReadyStepIndex, setTargetReadyStepIndex] = useState<number | null>(null);
    const [autoStartSuppressed, setAutoStartSuppressed] = useState<boolean>(false);
    const [activePlacement, setActivePlacement] = useState<Placement>('bottom');
    const tourRouteNavigationAllowedRef = useRef(false);

    const hasOrganization = Boolean(
        user?.active_organization_id
        || user?.organization_id
        || (Array.isArray(user?.organizations) && user.organizations.length > 0),
    );
    const isSuperAdmin = isSuperAdminUser(user);
    const canUseGuidedTour = isAuthenticated && !isSuperAdmin;
    const isAdmin = isActiveOrganizationAdmin(user);
    const projectRoute = getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.projectRoute);
    const currentProjectRoute = inferCurrentProjectRoute(location.pathname, location.search);

    const routeContext = useMemo<GuidedTourRouteContext>(
        () => ({ hasOrganization, isAdmin, projectRoute, currentProjectRoute }),
        [currentProjectRoute, hasOrganization, isAdmin, projectRoute],
    );

    const activeStepConfig = DASHBOARD_GUIDED_TOUR_STEPS[stepIndex];
    const activeRoute = activeStepConfig ? resolveGuidedTourRoute(activeStepConfig, routeContext) : '/dashboard';
    const activeTarget = activeStepConfig ? resolveGuidedTourTarget(activeStepConfig, routeContext) : 'body';

    const persistStepIndex = useCallback((nextStepIndex: number): void =>
    {
        const clamped = clampStepIndex(nextStepIndex);
        setStepIndex(clamped);
        setStoredTourStepIndex(clamped);
    }, []);

    const resolveCurrentPageStepIndex = useCallback((): number =>
    {
        const pageStepIndices = DASHBOARD_GUIDED_TOUR_STEPS
            .map((step, index) => ({ step, index }))
            .filter(({ step }) =>
                routePathMatches(resolveGuidedTourRoute(step, routeContext), location.pathname));

        if (pageStepIndices.length === 0)
        {
            return 0;
        }

        const firstPathMatch = pageStepIndices[0].index;

        // If the current page has a required visible field that is still empty,
        // start there instead of jumping to the most advanced visible control.
        for (const { step, index } of pageStepIndices)
        {
            if (!getMilestoneComplete(step.requiresMilestone) || !isRequiredCurrentPageTourStep(step.id))
            {
                continue;
            }
            const target = resolveGuidedTourTarget(step, routeContext);
            if (
                (isVisibleTourTarget(document.querySelector(target)) || document.querySelector(target) !== null)
                && !getStepActionComplete(step.id, hasOrganization)
            )
            {
                return index;
            }
        }

        // Most advanced step with a visible target and satisfied milestone
        for (let i = pageStepIndices.length - 1; i >= 0; i--)
        {
            const { step, index } = pageStepIndices[i];
            if (!getMilestoneComplete(step.requiresMilestone))
            {
                continue;
            }
            const target = resolveGuidedTourTarget(step, routeContext);
            if (isVisibleTourTarget(document.querySelector(target)))
            {
                return index;
            }
        }

        // Fall back: most advanced step whose target exists in the DOM with satisfied milestone
        for (let i = pageStepIndices.length - 1; i >= 0; i--)
        {
            const { step, index } = pageStepIndices[i];
            if (!getMilestoneComplete(step.requiresMilestone))
            {
                continue;
            }
            const target = resolveGuidedTourTarget(step, routeContext);
            if (document.querySelector(target) !== null)
            {
                return index;
            }
        }

        return firstPathMatch;
    }, [hasOrganization, location.pathname, routeContext]);

    const resolveReplayStartStepIndex = useCallback((): number =>
    {
        const hasCurrentPageMatch = DASHBOARD_GUIDED_TOUR_STEPS.some((step) =>
            routePathMatches(resolveGuidedTourRoute(step, routeContext), location.pathname));

        if (hasCurrentPageMatch)
        {
            return resolveCurrentPageStepIndex();
        }

        const billingDecisionCompleted = getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted) === 'true';

        if (isAdmin && !hasOrganization)
        {
            return getGuidedTourStepIndexById('organization-open-create');
        }

        if (isAdmin && !billingDecisionCompleted)
        {
            return getGuidedTourStepIndexById('billing-decision');
        }

        return getGuidedTourStepIndexById('project-name');
    }, [hasOrganization, isAdmin, location.pathname, resolveCurrentPageStepIndex, routeContext]);

    const completeGuidedTour = useCallback((): void =>
    {
        setRunning(false);
        setTargetReadyStepIndex(null);
        setAutoStartSuppressed(true);
        setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.completed, 'true');
        removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.skipped);
        removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested);
        setStoredTourStepIndex(DASHBOARD_GUIDED_TOUR_STEPS.length - 1);
        if (location.pathname !== '/dashboard')
        {
            navigate('/dashboard');
        }
    }, [location.pathname, navigate]);

    const skipGuidedTour = useCallback((): void =>
    {
        setRunning(false);
        setTargetReadyStepIndex(null);
        setAutoStartSuppressed(true);
        setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.skipped, 'true');
        removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested);

        if (isGuidedTourQuery(location.search))
        {
            navigate(`${location.pathname}${getSearchWithoutGuidedTour(location.search)}`, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    const startGuidedTour = useCallback((): void =>
    {
        if (!canUseGuidedTour)
        {
            return;
        }

        // Users who created their project outside the guided tour will not have
        // projectRoute in storage, so the projectCreated milestone check fails and
        // every project-page step is silently skipped.  Write the current project
        // route now so the milestone is satisfied before step resolution runs.
        if (currentProjectRoute && !getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.projectRoute))
        {
            setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.projectRoute, currentProjectRoute);
        }

        removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.completed);
        removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.skipped);
        setAutoStartSuppressed(false);
        setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested, 'true');
        tourRouteNavigationAllowedRef.current = true;
        persistStepIndex(resolveReplayStartStepIndex());
        setRunning(true);
    }, [canUseGuidedTour, currentProjectRoute, persistStepIndex, resolveReplayStartStepIndex]);

    const advanceToStep = useCallback((nextStepIndex: number): void =>
    {
        clearGuidedTourValidationHighlights();
        if (nextStepIndex >= DASHBOARD_GUIDED_TOUR_STEPS.length)
        {
            completeGuidedTour();
            return;
        }

        persistStepIndex(nextStepIndex);
        setTargetReadyStepIndex(null);
        tourRouteNavigationAllowedRef.current = true;
    }, [completeGuidedTour, persistStepIndex]);

    useEffect(() =>
    {
        if (!running)
        {
            clearGuidedTourValidationHighlights();
            return undefined;
        }

        function handleRequiredFieldInteraction(): void
        {
            window.setTimeout(clearSatisfiedGuidedTourValidationHighlights, 0);
        }

        document.addEventListener('input', handleRequiredFieldInteraction, true);
        document.addEventListener('change', handleRequiredFieldInteraction, true);
        document.addEventListener('click', handleRequiredFieldInteraction, true);

        return () =>
        {
            document.removeEventListener('input', handleRequiredFieldInteraction, true);
            document.removeEventListener('change', handleRequiredFieldInteraction, true);
            document.removeEventListener('click', handleRequiredFieldInteraction, true);
        };
    }, [running]);

    useEffect(() =>
    {
        if (loading || !canUseGuidedTour || running)
        {
            return;
        }

        const explicitGuidedTourRequest = isGuidedTourQuery(location.search);
        if (explicitGuidedTourRequest && autoStartSuppressed)
        {
            return;
        }

        if (explicitGuidedTourRequest)
        {
            removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.completed);
            removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.skipped);
        }

        const isFreshOrganizationTour = explicitGuidedTourRequest
            && location.pathname === '/admin/organizations'
            && !hasOrganization;

        if (isFreshOrganizationTour)
        {
            removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested);
            removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.organizationCreated);
            removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted);
            removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.projectRoute);
            setStoredTourStepIndex(0);
        }

        if (!explicitGuidedTourRequest && isDashboardGuidedTourDismissed())
        {
            return;
        }

        const replayRequested = getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested) === 'true';
        const billingDecisionCompleted = getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted) === 'true';
        const savedStepIndex = clampStepIndex(getStoredTourStepIndex());
        // Onboarding milestones persist after the user finishes setup.  They are
        // not an instruction to reopen the tour: doing so can leave the sidebar
        // inaccessible on later visits to Projects.  Only an explicit URL handoff
        // or a Help-initiated replay may resume the guided tour.
        const shouldResume = replayRequested || explicitGuidedTourRequest;

        if (!shouldResume)
        {
            return;
        }

        const savedStepConfig = DASHBOARD_GUIDED_TOUR_STEPS[savedStepIndex];
        const savedStepMatchesCurrentRoute = savedStepConfig
            ? routeMatches(resolveGuidedTourRoute(savedStepConfig, routeContext), location.pathname, location.search)
            : false;

        const isExplicitProjectOnboardingStart = explicitGuidedTourRequest
            && location.pathname === '/projects/new'
            && billingDecisionCompleted;
        const nextStepIndex = isExplicitProjectOnboardingStart
            ? getGuidedTourStepIndexById('project-name')
            : replayRequested && !explicitGuidedTourRequest
            ? savedStepIndex
            : savedStepMatchesCurrentRoute
                ? savedStepIndex
                : resolveCurrentPageStepIndex();

        setStepIndex(nextStepIndex);
        setRunning(true);
    }, [autoStartSuppressed, canUseGuidedTour, hasOrganization, loading, location.pathname, location.search, resolveCurrentPageStepIndex, routeContext, running]);

    useEffect(() =>
    {
        if (!isGuidedTourQuery(location.search))
        {
            setAutoStartSuppressed(false);
        }
    }, [location.search]);

    useEffect(() =>
    {
        if (!isSuperAdmin)
        {
            return;
        }

        setRunning(false);
        setTargetReadyStepIndex(null);
        removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested);
    }, [isSuperAdmin]);

    useEffect(() =>
    {
        if (!running || !activeStepConfig)
        {
            setTargetReadyStepIndex(null);
            setActivePlacement('bottom');
            return undefined;
        }

        if (!getMilestoneComplete(activeStepConfig.requiresMilestone))
        {
            setTargetReadyStepIndex(null);
            return undefined;
        }

        if (shouldSkipGuidedTourStep(activeStepConfig.id))
        {
            advanceToStep(getGuidedTourStepIndexById('project-step-one-next'));
            return undefined;
        }

        if (!routeMatches(activeRoute, location.pathname, location.search))
        {
            setTargetReadyStepIndex(null);

            // The early-access billing continuation is a page-owned action, so it
            // does not go through advanceToStep. Preserve the running tour for
            // that intentional handoff instead of treating it as sidebar/manual
            // navigation and suppressing the project-page restart.
            const isProjectOnboardingHandoff = location.pathname === '/projects/new'
                && isGuidedTourQuery(location.search)
                && getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted) === 'true';
            if (!tourRouteNavigationAllowedRef.current && isProjectOnboardingHandoff)
            {
                advanceToStep(getGuidedTourStepIndexById('project-name'));
                return undefined;
            }

            if (!tourRouteNavigationAllowedRef.current)
            {
                setRunning(false);
                setAutoStartSuppressed(true);
                removeTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested);
                if (isGuidedTourQuery(location.search))
                {
                    navigate(`${location.pathname}${getSearchWithoutGuidedTour(location.search)}`, { replace: true });
                }
                return undefined;
            }

            const storedStepIndex = clampStepIndex(getStoredTourStepIndex());
            if (storedStepIndex !== stepIndex)
            {
                persistStepIndex(storedStepIndex);
                return undefined;
            }

            tourRouteNavigationAllowedRef.current = false;
            navigate(activeRoute);
            return undefined;
        }

        tourRouteNavigationAllowedRef.current = false;

        if (activeStepConfig.prepareAction === 'openOrganizationCreateModal')
        {
            window.dispatchEvent(new Event(ORGANIZATION_CREATE_MODAL_EVENT));
        }

        if (activeStepConfig.prepareAction === 'showBillingPaymentForm')
        {
            window.dispatchEvent(new Event(BILLING_PAYMENT_FORM_EVENT));
        }

        if (activeStepConfig.prepareAction === 'showAzureDevOpsProjectFields')
        {
            window.dispatchEvent(new Event(PROJECT_WIZARD_SHOW_AZURE_EVENT));
        }

        if (activeStepConfig.projectWizardStep)
        {
            window.dispatchEvent(new CustomEvent(PROJECT_WIZARD_STEP_EVENT, {
                detail: { step: activeStepConfig.projectWizardStep },
            }));
        }

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = activeStepConfig.optionalTarget ? 8 : Number.POSITIVE_INFINITY;

        function checkTarget(): void
        {
            if (cancelled)
            {
                return;
            }

            const target = document.querySelector(activeTarget);
            setActivePlacement(resolveTooltipPlacement({
                target: activeTarget,
                title: activeStepConfig.title,
                content: activeStepConfig.content,
                id: activeStepConfig.id,
                placement: activeStepConfig.placement ?? 'bottom',
            }, target));

            if (isVisibleTourTarget(target))
            {
                setTargetReadyStepIndex(stepIndex);
                return;
            }

            attempts += 1;
            if (activeStepConfig.optionalTarget && attempts >= maxAttempts)
            {
                advanceToStep(stepIndex + 1);
            }
        }

        setTargetReadyStepIndex(null);
        checkTarget();
        const intervalId = window.setInterval(checkTarget, 250);

        return () =>
        {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [activeRoute, activeStepConfig, activeTarget, advanceToStep, location.pathname, location.search, navigate, persistStepIndex, running, stepIndex]);

    useEffect(() =>
    {
        if (targetReadyStepIndex !== stepIndex || !running)
        {
            return undefined;
        }

        const id = window.setTimeout(() =>
        {
            const target = document.querySelector(activeTarget);
            if (target && isVisibleTourTarget(target))
            {
                focusTourTarget(target);
            }
        }, 150);

        return () => window.clearTimeout(id);
    }, [targetReadyStepIndex, stepIndex, running, activeTarget]);

    useEffect(() =>
    {
        function handleProjectWizardStepChanged(event: Event): void
        {
            if (!running)
            {
                return;
            }

            const detail = (event as CustomEvent<{ step?: 1 | 2 | 3 | 4 }>).detail;
            const wizardStep = detail?.step;
            const currentStep = DASHBOARD_GUIDED_TOUR_STEPS[stepIndex];
            if (!wizardStep || !currentStep?.id.startsWith('project-'))
            {
                return;
            }

            const nextStepIndex = getProjectWizardStepStartIndex(wizardStep, routeContext, location.pathname);
            if (nextStepIndex >= 0 && nextStepIndex !== stepIndex)
            {
                advanceToStep(nextStepIndex);
            }
        }

        window.addEventListener(PROJECT_WIZARD_STEP_CHANGED_EVENT, handleProjectWizardStepChanged);
        return () => window.removeEventListener(PROJECT_WIZARD_STEP_CHANGED_EVENT, handleProjectWizardStepChanged);
    }, [advanceToStep, location.pathname, routeContext, running, stepIndex]);

    useEffect(() =>
    {
        function handleRequirementsReviewOpened(): void
        {
            if (!running)
            {
                return;
            }

            const currentStep = DASHBOARD_GUIDED_TOUR_STEPS[stepIndex];
            if (currentStep?.id !== 'generate-tests-button')
            {
                return;
            }

            advanceToStep(getGuidedTourStepIndexById('generate-tests-confirm'));
        }

        window.addEventListener(REQUIREMENTS_GENERATE_REVIEW_EVENT, handleRequirementsReviewOpened);
        return () => window.removeEventListener(REQUIREMENTS_GENERATE_REVIEW_EVENT, handleRequirementsReviewOpened);
    }, [advanceToStep, running, stepIndex]);

    const joyrideSteps = useMemo<Step[]>(() =>
    {
        if (!activeStepConfig)
        {
            return [];
        }

        return [
            {
                target: activeTarget,
                title: activeStepConfig.title,
                content: activeStepConfig.content,
                id: activeStepConfig.id,
                placement: activePlacement,
                data: {
                    stepIndex,
                    totalSteps: DASHBOARD_GUIDED_TOUR_STEPS.length,
                    hasOrganization,
                    requiresMilestone: activeStepConfig.requiresMilestone,
                },
            },
        ];
    }, [activePlacement, activeStepConfig, activeTarget, hasOrganization, stepIndex]);

    const handleJoyrideEvent = useCallback((data: EventData): void =>
    {
        const { action, status, type } = data;

        if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE || action === ACTIONS.SKIP)
        {
            skipGuidedTour();
            return;
        }

        if (type !== EVENTS.STEP_AFTER && status !== STATUS.FINISHED)
        {
            return;
        }

        if (action === ACTIONS.PREV)
        {
            advanceToStep(stepIndex - 1);
            return;
        }

        const currentStep = DASHBOARD_GUIDED_TOUR_STEPS[stepIndex];
        if (!getMilestoneComplete(currentStep?.requiresMilestone) || !getStepActionComplete(currentStep?.id, hasOrganization))
        {
            markGuidedTourRequiredFields(currentStep?.id);
            setTargetReadyStepIndex(stepIndex);
            return;
        }

        advanceToStep(getNextStepIndex(currentStep?.id, stepIndex));
    }, [advanceToStep, hasOrganization, skipGuidedTour, stepIndex]);

    const contextValue = useMemo<GuidedTourContextValue>(
        () => ({
            startGuidedTour,
            skipGuidedTour,
            isGuidedTourRunning: running,
            isGuidedTourAvailable: canUseGuidedTour,
        }),
        [canUseGuidedTour, running, skipGuidedTour, startGuidedTour],
    );

    return (
        <GuidedTourContext.Provider value={contextValue}>
            {children}
            <Joyride
                key={`${activeStepConfig?.id ?? 'guided-tour'}:${activePlacement}`}
                continuous
                onEvent={handleJoyrideEvent}
                options={{
                    arrowColor: '#ffffff',
                    backgroundColor: '#ffffff',
                    buttons: ['back', 'close', 'primary', 'skip'],
                    closeButtonAction: 'skip',
                    dismissKeyAction: false,
                    overlayClickAction: false,
                    overlayColor: 'rgba(17, 24, 39, 0.36)',
                    primaryColor: '#0d6efd',
                    showProgress: true,
                    skipBeacon: true,
                    targetWaitTimeout: 0,
                    textColor: '#1f2937',
                    zIndex: 3000,
                }}
                floatingOptions={{ hideArrow: true }}
                run={canUseGuidedTour && running && targetReadyStepIndex === stepIndex && joyrideSteps.length > 0}
                scrollToFirstStep
                steps={joyrideSteps}
                styles={{
                    tooltip: {
                        borderRadius: 10,
                        boxShadow: '0 18px 48px rgba(15, 23, 42, 0.22)',
                    },
                    tooltipTitle: {
                        fontSize: '1rem',
                        fontWeight: 700,
                        marginBottom: 8,
                    },
                    tooltipContent: {
                        fontSize: '0.9rem',
                        lineHeight: 1.55,
                    },
                }}
                locale={{
                    back: 'Previous',
                    close: 'Close',
                    last: 'Next',
                    next: 'Next',
                    nextWithProgress: 'Next',
                    skip: 'Skip Tutorial',
                }}
                tooltipComponent={GuidedTourTooltip}
            />
        </GuidedTourContext.Provider>
    );
}

export {
    BILLING_PAYMENT_FORM_EVENT,
    ORGANIZATION_CREATE_MODAL_EVENT,
    PROJECT_WIZARD_SHOW_AZURE_EVENT,
    PROJECT_WIZARD_STEP_CHANGED_EVENT,
    PROJECT_WIZARD_STEP_EVENT,
    REQUIREMENTS_GENERATE_REVIEW_EVENT,
};
